import type { ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { canAssignRoles, canSeeInventory, type AppRole } from '@infras/shared-types';
import { useAuth } from './lib/auth';
import { useMe } from './lib/useMe';
import { usePushReconcile } from './lib/usePushReconcile';
import { LoginPage } from './pages/LoginPage';
import { ActivityPage } from './pages/ActivityPage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import { InventoryPage } from './pages/InventoryPage';
import { RepoDetailPage } from './pages/RepoDetailPage';
import { ClustersPage } from './pages/ClustersPage';
import { ClusterDetailPage } from './pages/ClusterDetailPage';
import { SecretsPage } from './pages/SecretsPage';
import { SecretDetailPage } from './pages/SecretDetailPage';
import { SchedulerPage } from './pages/SchedulerPage';
import { SchedulerJobPage } from './pages/SchedulerJobPage';
import { CatalogPage } from './pages/CatalogPage';
import { ErrorCodesPage } from './pages/ErrorCodesPage';
import { ErrorCodeDetailPage } from './pages/ErrorCodeDetailPage';
import { AccountPage } from './pages/AccountPage';
import { UsersRolesPage } from './pages/UsersRolesPage';
import { AccessReviewersPage } from './pages/AccessReviewersPage';
import { RequestQuestionsPage } from './pages/RequestQuestionsPage';
import { DocsPage } from './pages/DocsPage';
import { AccessRequestsPage } from './pages/AccessRequestsPage';
import { AccessRequestNewPage } from './pages/AccessRequestNewPage';
import { AccessRequestDetailPage } from './pages/AccessRequestDetailPage';
import { AtlasWhitelistPage } from './pages/AtlasWhitelistPage';
import { AtlasProjectPage } from './pages/AtlasProjectPage';
import { AtlasMappingsPage } from './pages/AtlasMappingsPage';
import { AgentsPage } from './pages/AgentsPage';
import { AgentCommandsPage } from './pages/AgentCommandsPage';
import { AgentCommandPage } from './pages/AgentCommandPage';
import { VpnPeersPage } from './pages/VpnPeersPage';
import { VpnSubnetsPage } from './pages/VpnSubnetsPage';
import { VpnConnectionAuditPage } from './pages/VpnConnectionAuditPage';
import { AppShell } from './components/AppShell';
import { Logo } from './components/Logo';

export function App() {
  const auth = useAuth();

  if (!auth.authed) {
    return <LoginPage onCredential={auth.loginWithGoogle} />;
  }

  return (
    <BrowserRouter>
      <RoleShell email={auth.user?.email} onLogout={auth.logout} />
    </BrowserRouter>
  );
}

/**
 * `/graph` was its own page before the graph became a view of `/repos`. Kept as a
 * redirect because Global Search hits and Slack links point at it — and the
 * QUERY STRING must survive: those links carry ?service= / ?cluster=, which is
 * what the graph focuses on. Dropping it would land every search hit on an
 * unfocused graph.
 */
function GraphRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('view', 'graph');
  return <Navigate to={`/repos?${params.toString()}`} replace />;
}

/**
 * Chrome with no navigation — the only honest shell before `me` resolves, since
 * every nav item is role-gated and we don't yet know the role. Guessing one
 * would flash inventory links at a new member.
 */
function BareShell({ children }: { children: ReactNode }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/" className="brand-link" aria-label="OptInfra home">
          <Logo />
        </Link>
        <div className="spacer" />
      </header>
      <div className="app-body">
        <main className="app-main">
          <div className="page">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Role-aware shell — waits for the server-verified `me` (no flash of inventory
 * nav for a new member), then renders the surface the role permits:
 *   inventory roles → full nav + all routes;
 *   new_member      → Get Started + docs + Settings only (RolesGuard blocks the
 *                     data anyway — this is UX, the API is the enforcement).
 */
function RoleShell({ email, onLogout }: { email?: string; onLogout: () => void }) {
  const { me, loading, error } = useMe();
  // Re-claim this browser's push subscription for the CURRENT user (shared machines).
  // Above the early returns so it is never a conditional hook.
  usePushReconcile(me?.email);

  // Before `me` resolves there is no role, so the real nav can't be built — but
  // dropping the user onto an unstyled page reads as a crash. Render the chrome
  // without nav instead, so the brand and a sign-out route stay present.
  if (loading) return <BareShell>Loading your profile…</BareShell>;
  if (error || !me) {
    return (
      <BareShell>
        <p className="error">
          We couldn&apos;t load your profile, so the app doesn&apos;t know what you have access to.
        </p>
        <p className="muted">
          Signing out and back in usually fixes it. If it keeps happening, ask in #devops-infra —
          your access itself is unaffected.
        </p>
        {error && (
          <details style={{ marginTop: 12 }}>
            <summary className="muted">Details</summary>
            <p className="error">{error.message}</p>
          </details>
        )}
        <button className="btn" onClick={onLogout} style={{ marginTop: 12 }}>
          Sign out
        </button>
      </BareShell>
    );
  }

  const inventory = canSeeInventory(me.role);
  // The connection-audit log is the SOC2 audit-of-record → root only, matching
  // the @RequireRole('root') on the backing query (this is UX; the API enforces).
  const isRoot = canAssignRoles(me.role);

  return (
    <AppShell role={me.role} email={email ?? me.email} onLogout={onLogout}>
      <Routes>
        {/* First login → Get Started (onboard the agent); returning users land on
            their home — inventory for data roles, else Get Started. */}
        <Route path="/" element={<Home me={me} />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/docs/:section" element={<DocsPage />} />
        {/* back-compat for the old top-level doc routes */}
        <Route path="/get-started" element={<Navigate to="/docs/get-started" replace />} />
        <Route path="/mcp" element={<Navigate to="/docs/mcp" replace />} />
        {inventory && (
          <>
            <Route path="/repos" element={<InventoryPage />} />
            <Route path="/repos/:id" element={<RepoDetailPage />} />
            <Route path="/clusters" element={<ClustersPage />} />
            <Route path="/clusters/:name" element={<ClusterDetailPage />} />
            <Route path="/graph" element={<GraphRedirect />} />
            {/* Full-results view for the ⌘K palette's overflow row. */}
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/secrets" element={<SecretsPage />} />
            <Route path="/secrets/:name" element={<SecretDetailPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/scheduler" element={<SchedulerPage />} />
            <Route path="/scheduler/:name" element={<SchedulerJobPage />} />
            <Route path="/atlas-whitelist" element={<AtlasWhitelistPage />} />
            {/* `mappings` is declared BEFORE `:projectKey` — react-router ranks
                static segments above dynamic ones, but the explicit order also
                documents that "mappings" is not a project key. */}
            <Route path="/atlas-whitelist/mappings" element={<AtlasMappingsPage />} />
            <Route path="/atlas-whitelist/:projectKey" element={<AtlasProjectPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            {/* command plane — one nav entry (Agents); these hang off it */}
            <Route path="/agents/commands" element={<AgentCommandsPage />} />
            <Route path="/agents/commands/:id" element={<AgentCommandPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/error-codes" element={<ErrorCodesPage />} />
            <Route path="/error-codes/:code" element={<ErrorCodeDetailPage />} />
            <Route path="/catalog/:name" element={<CatalogPage />} />
            {/* VPN Network views — read-only; enrollment stays in staff-app. */}
            <Route path="/vpn/peers" element={<VpnPeersPage />} />
            <Route path="/vpn/subnets" element={<VpnSubnetsPage />} />
            {isRoot && (
              <Route path="/vpn/connection-audit" element={<VpnConnectionAuditPage />} />
            )}
          </>
        )}
        {/* access requests — every role (new members are the main requesters) */}
        <Route path="/access" element={<AccessRequestsPage />} />
        <Route path="/access/new" element={<AccessRequestNewPage />} />
        <Route path="/access/:id" element={<AccessRequestDetailPage />} />

        {/* Administration is four real pages at the SAME paths the sidebar, Slack
            links and docs already point at. The tabbed SettingsPage is gone: the
            sidebar navigates these now, so a tab strip was a second nav for the
            same four destinations. */}
        <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
        <Route path="/settings/profile" element={<AccountPage />} />
        {/* PATs used to be their own tab on your own page; they're on Account now. */}
        <Route path="/settings/access" element={<Navigate to="/settings/profile" replace />} />
        <Route path="/settings/team" element={<UsersRolesPage />} />
        <Route path="/settings/reviewers" element={<AccessReviewersPage />} />
        <Route path="/settings/questions" element={<RequestQuestionsPage />} />
        {/* any other /settings/* → your own page, not a 404 */}
        <Route path="/settings/*" element={<Navigate to="/settings/profile" replace />} />
        <Route path="*" element={<Navigate to="/docs" replace />} />
      </Routes>
    </AppShell>
  );
}

/**
 * What `/` does — a REDIRECT, not a page. First visit (per browser + email) → Get
 * Started, and a new member stays there permanently since it is their only surface.
 * Everyone else → `/repos`.
 *
 * Phase 2a made `/` render the Overview. Reverted on request: the Overview isn't
 * ready to be the thing that greets you, and the repos table is what people came
 * for. The page is not deleted — it is unlinked at `/overview`, so the freshness
 * cards, the waiting-on-you band, the change-events feed and the unowned-repos
 * count all still work and can be promoted when it's ready.
 *
 * The decision is cached per email at module scope so React StrictMode's dev
 * double-mount — which would otherwise set the flag on the first mount and flip a
 * first-timer to the returning path on the remount — can't change the outcome.
 */
const landingDecided: Record<string, boolean> = {};
function Home({ me }: { me: { email: string; role: AppRole } }) {
  const email = me.email.toLowerCase();
  if (landingDecided[email] === undefined) {
    const key = `infras.onboarded.${email}`;
    landingDecided[email] = !localStorage.getItem(key);
    localStorage.setItem(key, '1');
  }
  const firstVisit = landingDecided[email];
  if (firstVisit || !canSeeInventory(me.role)) return <Navigate to="/docs/get-started" replace />;
  return <Navigate to="/repos" replace />;
}
